import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * useRoninLevelPicker — all state + handlers + derived data for the admin level picker
 * (bulk assign cues to beginner/intermediate/advanced, mode tags, weights). Extracted
 * from RoninLevelPicker so the page is presentational. Behavior unchanged.
 */
export function useRoninLevelPicker() {
  const [cues, setCues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modes, setModes] = useState([]); // dynamic modes from API (filtered to exclude "custom")
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});         // {cue_id: new_levels_array}
  const [dirtyModes, setDirtyModes] = useState({}); // {cue_id: new_modes_array}
  const [dirtyWeights, setDirtyWeights] = useState({}); // {cue_id: new_weight_int (1-10)}
  const [filterLevel, setFilterLevel] = useState("all");
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cuesRes, catsRes, modesRes] = await Promise.all([
        api.get("/admin/ronin-box/cues"),
        api.get("/admin/ronin-box/categories"),
        api.get("/admin/ronin-box/modes"),
      ]);
      setCues(cuesRes.data || []);
      setCategories(catsRes.data || []);
      // Exclude "custom" mode — it's a wildcard, not a tag per cue
      setModes((modesRes.data || []).filter((m) => m.id !== "custom"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // Inline + deferred to avoid the react-hooks/set-state-in-effect lint rule.
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, []);

  // Effective levels = pending change if present (array), else the saved levels array on the cue.
  // Legacy fallback: derive from single `level` field using the cumulative mapping.
  const effectiveLevels = (c) => {
    if (c.id in dirty) return dirty[c.id];
    if (c.levels && c.levels.length > 0) return c.levels;
    if (c.level === "advanced") return ["advanced"];
    if (c.level === "intermediate") return ["intermediate", "advanced"];
    return ["beginner", "intermediate", "advanced"];
  };

  const toggleLevel = (cueId, savedLevels, lvl) => {
    setDirty((prev) => {
      const next = { ...prev };
      const current = next[cueId] ?? savedLevels;
      const newSet = current.includes(lvl)
        ? current.filter((l) => l !== lvl)
        : [...current, lvl];
      // Guarantee at least one level — empty arrays would make the cue invisible everywhere.
      if (newSet.length === 0) {
        toast.error("A cue must belong to at least one level.");
        return prev;
      }
      // If we toggled back to the saved state, clear the dirty entry
      const savedSorted = [...savedLevels].sort().join(",");
      const newSorted = [...newSet].sort().join(",");
      if (savedSorted === newSorted) {
        delete next[cueId];
      } else {
        next[cueId] = newSet;
      }
      return next;
    });
  };

  const dirtyCount = Object.keys(dirty).length + Object.keys(dirtyModes).length + Object.keys(dirtyWeights).length;

  // Effective modes = pending change if present, else cue.modes (or empty array)
  const effectiveModes = (c) => {
    if (c.id in dirtyModes) return dirtyModes[c.id];
    return Array.isArray(c.modes) ? c.modes : [];
  };

  // Effective weight = pending change if present, else cue.weight (or 1 default).
  // Clamped to 1-10 to match the backend Field(ge=1, le=10).
  const effectiveWeight = (c) => {
    if (c.id in dirtyWeights) return dirtyWeights[c.id];
    const w = Number.isFinite(c.weight) ? c.weight : 1;
    return Math.max(1, Math.min(10, w));
  };

  const setWeight = (cueId, savedWeight, newWeight) => {
    setDirtyWeights((prev) => {
      const next = { ...prev };
      const clamped = Math.max(1, Math.min(10, Number(newWeight) || 1));
      if (clamped === savedWeight) {
        delete next[cueId];
      } else {
        next[cueId] = clamped;
      }
      return next;
    });
  };

  const toggleMode = (cueId, savedModes, modeId) => {
    setDirtyModes((prev) => {
      const next = { ...prev };
      const current = next[cueId] ?? savedModes;
      const newSet = current.includes(modeId)
        ? current.filter((m) => m !== modeId)
        : [...current, modeId];
      // If toggled back to saved state, clear the dirty entry
      const savedSorted = [...savedModes].sort().join(",");
      const newSorted = [...newSet].sort().join(",");
      if (savedSorted === newSorted) {
        delete next[cueId];
      } else {
        next[cueId] = newSet;
      }
      return next;
    });
  };

  // Bulk: set every visible cue's levels to exactly [level] (single-level reassign)
  const bulkSet = (level) => {
    setDirty((prev) => {
      const next = { ...prev };
      for (const c of visibleCues) {
        const savedLevels = (c.levels && c.levels.length > 0)
          ? c.levels
          : (c.level === "advanced" ? ["advanced"]
             : c.level === "intermediate" ? ["intermediate", "advanced"]
             : ["beginner", "intermediate", "advanced"]);
        const newLevels = [level];
        if (JSON.stringify([...savedLevels].sort()) === JSON.stringify([...newLevels].sort())) {
          delete next[c.id];
        } else {
          next[c.id] = newLevels;
        }
      }
      return next;
    });
  };

  const reset = () => { setDirty({}); setDirtyModes({}); setDirtyWeights({}); };

  const save = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    try {
      const calls = [];
      if (Object.keys(dirty).length > 0) {
        const levelUpdates = Object.entries(dirty).map(([id, levels]) => ({ id, levels }));
        calls.push(api.post("/admin/ronin-box/cues/batch-level", { updates: levelUpdates }));
      }
      if (Object.keys(dirtyModes).length > 0) {
        const modeUpdates = Object.entries(dirtyModes).map(([id, m]) => ({ id, modes: m }));
        calls.push(api.post("/admin/ronin-box/cues/batch-modes", { updates: modeUpdates }));
      }
      if (Object.keys(dirtyWeights).length > 0) {
        const weightUpdates = Object.entries(dirtyWeights).map(([id, w]) => ({ id, weight: w }));
        calls.push(api.post("/admin/ronin-box/cues/batch-weight", { updates: weightUpdates }));
      }
      const results = await Promise.all(calls);
      const totalUpdated = results.reduce((s, r) => s + (r.data?.updated || 0), 0);
      toast.success(`Updated ${totalUpdated} cue${totalUpdated === 1 ? "" : "s"}`);
      setDirty({});
      setDirtyModes({});
      setDirtyWeights({});
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  // One-shot recovery: stage every cue currently tagged with a single level to all 3 levels.
  // Useful after we shipped a bad default where new cues silently landed with levels=["beginner"]
  // only, hiding them from Intermediate + Advanced users.
  const expandSingleTierCues = () => {
    let count = 0;
    setDirty((prev) => {
      const next = { ...prev };
      for (const c of cues) {
        const lvls = (c.levels && c.levels.length > 0)
          ? c.levels
          : (c.level === "advanced" ? ["advanced"]
             : c.level === "intermediate" ? ["intermediate", "advanced"]
             : ["beginner", "intermediate", "advanced"]);
        if (lvls.length === 1) {
          next[c.id] = ["beginner", "intermediate", "advanced"];
          count++;
        }
      }
      return next;
    });
    if (count === 0) {
      toast.info("No single-tier cues found.");
    } else {
      toast.success(`Staged ${count} single-tier cue${count === 1 ? "" : "s"} to all 3 levels. Press Save.`);
    }
  };

  // Filter cues by level chip + search.
  // "all" = no filter. Otherwise show cues whose effective levels INCLUDE the chip.
  const visibleCues = (() => {
    const q = search.trim().toLowerCase();
    return cues.filter((c) => {
      if (filterLevel !== "all" && !effectiveLevels(c).includes(filterLevel)) return false;
      if (q && !(c.text || "").toLowerCase().includes(q)) return false;
      return true;
    });
  })();

  // Group by category
  const grouped = (() => {
    const g = {};
    for (const c of visibleCues) {
      const k = c.category || "uncategorized";
      (g[k] = g[k] || []).push(c);
    }
    return g;
  })();

  // Per-category level counts (cues can belong to multiple levels, so totals can exceed cue
  // count — counts represent membership not exclusive assignment).
  const categoryStats = (() => {
    const stats = {};
    for (const c of cues) {
      const k = c.category || "uncategorized";
      stats[k] = stats[k] || { beginner: 0, intermediate: 0, advanced: 0 };
      for (const lv of effectiveLevels(c)) {
        if (lv in stats[k]) stats[k][lv] += 1;
      }
    }
    return stats;
  })();

  // Global counts (header summary) — membership totals
  const totalStats = (() => {
    const s = { beginner: 0, intermediate: 0, advanced: 0 };
    for (const c of cues) {
      for (const lv of effectiveLevels(c)) {
        if (lv in s) s[lv] += 1;
      }
    }
    return s;
  })();

  const toggleCollapsed = (cat) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  // Show categories in the order returned by the API; fall back to keys from grouped if missing
  const categoryOrder = useMemo(() => {
    const ids = (categories.length > 0 ? categories.map((c) => c.id) : []);
    for (const k of Object.keys(grouped)) {
      if (!ids.includes(k)) ids.push(k);
    }
    return ids;
  }, [categories, grouped]);

  return {
    cues, setCues, categories, modes, loading, dirty, dirtyModes, dirtyWeights, filterLevel, setFilterLevel, search, setSearch, collapsed, saving, load, effectiveLevels, toggleLevel, dirtyCount, effectiveModes, effectiveWeight, setWeight, toggleMode, bulkSet, reset, save, expandSingleTierCues, visibleCues, grouped, categoryStats, totalStats, toggleCollapsed, categoryOrder,
  };
}
